/**
 * SnowMachineDisplay Component
 * 
 * Displays the snow simulation canvas. This component imports the
 * snowmachine module and initializes the SnowSimulation instance.
 */

import { useRef, useEffect } from 'react';
import { getActiveTheme } from '../config/seasonalFeatures';
import { SnowSimulation } from '../snowmachine/snowmachine';

// Global singleton to persist simulation across component remounts
let globalSimulationInstance = null;
let globalCanvasId = 'snowCanvas';
let previousMobileState = null; // Track previous mobile state to detect threshold crossings

export default function SnowMachineDisplay({ simulationRef, onSimulationReady, controlsReady, theme: themeProp }) {
  const theme = themeProp || getActiveTheme();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const initializedRef = useRef(false);

  // Helper function to detect mobile devices
  const isMobile = () => {
    return window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  // Function to restart simulation when switching mobile/desktop modes
  const restartSimulation = (mobile) => {
    if (!globalSimulationInstance) return;
    
    // Preserve current control values before restart
    const flakesPerMinuteSlider = document.getElementById('flakesPerMinuteSlider');
    const windSlider = document.getElementById('windSlider');
    const minFallSpeedSlider = document.getElementById('minFallSpeedSlider');
    const maxFallSpeedSlider = document.getElementById('maxFallSpeedSlider');
    const smallestPiecesSlider = document.getElementById('smallestPiecesSlider');
    const biggestPiecesSlider = document.getElementById('biggestPiecesSlider');
    
    const preservedValues = {
      flakesPerMinute: flakesPerMinuteSlider ? parseInt(flakesPerMinuteSlider.value) : null,
      wind: windSlider ? parseFloat(windSlider.value) : null,
      minFallSpeed: minFallSpeedSlider ? parseFloat(minFallSpeedSlider.value) : null,
      maxFallSpeed: maxFallSpeedSlider ? parseFloat(maxFallSpeedSlider.value) : null,
      smallestPieces: smallestPiecesSlider ? parseInt(smallestPiecesSlider.value) : null,
      biggestPieces: biggestPiecesSlider ? parseInt(biggestPiecesSlider.value) : null,
      snowMode: globalSimulationInstance.snowMode
    };
    
    // Stop current snow and spawning
    if (globalSimulationInstance.stopSnow) {
      globalSimulationInstance.stopSnow();
    }
    if (globalSimulationInstance.stopSpawning) {
      globalSimulationInstance.stopSpawning();
    }
    
    // Clear intervals
    if (globalSimulationInstance.spawnInterval) {
      clearInterval(globalSimulationInstance.spawnInterval);
      globalSimulationInstance.spawnInterval = null;
    }
    if (globalSimulationInstance.manualSpawnInterval) {
      clearInterval(globalSimulationInstance.manualSpawnInterval);
      globalSimulationInstance.manualSpawnInterval = null;
    }
    
    // Clear particles and clouds
    globalSimulationInstance.particles = [];
    globalSimulationInstance.clouds = [];
    if (globalSimulationInstance.rockets) globalSimulationInstance.rockets = [];
    if (globalSimulationInstance.sparks) globalSimulationInstance.sparks = [];
    
    // Update canvas size
    const container = containerRef.current;
    const maxWidth = Math.min(container.clientWidth || 1600, 1600);
    const aspectRatio = mobile ? 0.6 : (600 / 1600);
    const height = maxWidth * aspectRatio;
    
    const canvas = canvasRef.current;
    canvas.width = maxWidth;
    canvas.height = height;
    
    // Update simulation dimensions and mobile flag
    globalSimulationInstance.width = maxWidth;
    globalSimulationInstance.height = height;
    globalSimulationInstance.isMobile = mobile;
    if (globalSimulationInstance.behavior === 'fireworks') {
      globalSimulationInstance.fireworkPadsPerBattery = mobile ? 2 : 3;
    }
    
    // Recreate grid and structures with new mobile state
    globalSimulationInstance.grid = globalSimulationInstance.createGrid();
    globalSimulationInstance.createDefaultStructures();
    if (
      globalSimulationInstance.behavior === 'fireworks' &&
      globalSimulationInstance.pickFireworkLaunchSites
    ) {
      globalSimulationInstance.pickFireworkLaunchSites();
    }
    
    // Restore preserved values
    if (preservedValues.flakesPerMinute !== null) {
      globalSimulationInstance.baseFlakesPerMinute = preservedValues.flakesPerMinute;
      globalSimulationInstance.flakesPerMinute = preservedValues.flakesPerMinute;
    }
    if (preservedValues.wind !== null) {
      globalSimulationInstance.windStrength = preservedValues.wind;
    }
    if (preservedValues.minFallSpeed !== null) {
      globalSimulationInstance.minFallSpeed = preservedValues.minFallSpeed;
    }
    if (preservedValues.maxFallSpeed !== null) {
      globalSimulationInstance.maxFallSpeed = preservedValues.maxFallSpeed;
    }
    if (preservedValues.smallestPieces !== null) {
      globalSimulationInstance.smallestPieces = preservedValues.smallestPieces;
    }
    if (preservedValues.biggestPieces !== null) {
      globalSimulationInstance.biggestPieces = preservedValues.biggestPieces;
    }
    
    // Re-setup controls to ensure event listeners are still attached
    // Use a small delay to ensure DOM is ready
    setTimeout(() => {
      if (globalSimulationInstance.setupControls) {
        globalSimulationInstance.setupControls();
      }
      
      // Restart the simulation
      globalSimulationInstance.isRunning = true;
      if (preservedValues.snowMode && globalSimulationInstance.startSnow) {
        globalSimulationInstance.startSnow();
      }
    }, 50);
  };

  // Initialize simulation once controls are ready
  useEffect(() => {
    // If we already have a global simulation instance, reuse it
    if (globalSimulationInstance && globalSimulationInstance.canvas) {
      const canvas = document.getElementById(globalCanvasId);
      if (canvas && canvas === globalSimulationInstance.canvas) {
        // Simulation already exists and canvas is still in DOM
        if (simulationRef) {
          simulationRef.current = globalSimulationInstance;
        }
        if (onSimulationReady) {
          onSimulationReady(globalSimulationInstance);
        }
        initializedRef.current = true;
        return;
      }
    }

    if (!canvasRef.current || !controlsReady || initializedRef.current) return;

    // Verify that required control elements exist in the DOM
    const requiredElements = theme.behavior === 'fireworks'
      ? ['clearBtn', 'rainBtn', 'flakesPerMinuteSlider', 'windSlider', 'fwEffectGrid']
      : [
          'clearBtn',
          'rainBtn',
          'flakesPerMinuteSlider',
          'windSlider',
          'minFallSpeedSlider',
          'maxFallSpeedSlider',
          'smallestPiecesSlider',
          'biggestPiecesSlider',
        ];

    const checkElementsExist = () => {
      return requiredElements.every(id => document.getElementById(id) !== null);
    };

    // Wait for elements to be in DOM with retry logic
    let retryCount = 0;
    const maxRetries = 40; // 2 seconds max wait (40 * 50ms)
    
    const initSimulation = () => {
      const elementsExist = checkElementsExist();
      
      if (!elementsExist) {
        retryCount++;
        if (retryCount < maxRetries) {
          // Elements not ready yet, retry after a short delay
          setTimeout(initSimulation, 50);
          return;
        } else {
          // Elements not found after max retries
          return;
        }
      }

      // Double-check one more time with a small delay to ensure DOM is stable
      setTimeout(() => {
        if (!checkElementsExist()) {
          setTimeout(initSimulation, 100);
          return;
        }

        // Dynamically set canvas size based on container and device type
        const container = containerRef.current;
        const mobile = isMobile();
        const maxWidth = Math.min(container.clientWidth || 1600, 1600);
        // Use taller aspect ratio on mobile for better visibility
        const aspectRatio = mobile ? 0.6 : (600 / 1600); // 0.6 on mobile, 0.375 on desktop
        const height = maxWidth * aspectRatio;

        const canvas = canvasRef.current;
        canvas.width = maxWidth;
        canvas.height = height;

        // Initialize SnowSimulation with mobile flag
        try {
          if (!initializedRef.current && !globalSimulationInstance) {
            initializedRef.current = true; // Prevent multiple initializations
            const sim = new SnowSimulation(globalCanvasId, mobile, theme);
            
            // Store globally to persist across remounts
            globalSimulationInstance = sim;
            previousMobileState = mobile; // Track initial mobile state
            
            if (simulationRef) {
              simulationRef.current = sim;
            }
            if (onSimulationReady) {
              onSimulationReady(sim);
            }
          } else if (globalSimulationInstance) {
            // Reuse existing simulation
            if (simulationRef) {
              simulationRef.current = globalSimulationInstance;
            }
            if (onSimulationReady) {
              onSimulationReady(globalSimulationInstance);
            }
            initializedRef.current = true;
          }
        } catch (error) {
          initializedRef.current = false; // Allow retry on error
        }
      }, 100); // Small delay to ensure DOM is fully stable
    };

    // Start initialization check - only after all conditions are met
    initSimulation();

    // Cleanup on unmount - but only if this is truly unmounting (not just navigating)
    return () => {
      // Don't clean up the simulation on navigation - only on actual page unload
      // The simulation will persist across SPA navigation
      initializedRef.current = false; // Reset flag for potential re-initialization
    };
  }, [controlsReady, simulationRef, onSimulationReady, theme]);

  // Cleanup simulation only on actual page unload (not SPA navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (globalSimulationInstance) {
        // Stop the simulation
        if (globalSimulationInstance.isRunning !== undefined) {
          globalSimulationInstance.isRunning = false;
        }
        if (globalSimulationInstance.stop) {
          globalSimulationInstance.stop();
        }
        // Clear intervals
        if (globalSimulationInstance.spawnInterval) {
          clearInterval(globalSimulationInstance.spawnInterval);
        }
        if (globalSimulationInstance.manualSpawnInterval) {
          clearInterval(globalSimulationInstance.manualSpawnInterval);
        }
        globalSimulationInstance = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      // Debounce resize events
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!containerRef.current || !canvasRef.current) return;
        
        const mobile = isMobile();
        const container = containerRef.current;
        const maxWidth = Math.min(container.clientWidth || 1600, 1600);
        const aspectRatio = mobile ? 0.6 : (600 / 1600);
        const height = maxWidth * aspectRatio;

        const canvas = canvasRef.current;
        
        // Check if we've crossed the mobile/desktop threshold
        const crossedThreshold = previousMobileState !== null && previousMobileState !== mobile;
        
        if (crossedThreshold && globalSimulationInstance) {
          // Restart simulation when switching between mobile/desktop
          restartSimulation(mobile);
          previousMobileState = mobile;
          return;
        }
        
        // Normal resize - just update canvas size, don't recreate everything
        canvas.width = maxWidth;
        canvas.height = height;

        // Update simulation dimensions if it exists (without recreating grid)
        if (simulationRef?.current && !crossedThreshold) {
          simulationRef.current.width = maxWidth;
          simulationRef.current.height = height;
          // Re-lay mortar pads / viewing zone for the new canvas size
          if (
            simulationRef.current.behavior === 'fireworks' &&
            typeof simulationRef.current.pickFireworkLaunchSites === 'function'
          ) {
            simulationRef.current.pickFireworkLaunchSites();
          }
        }
        
        // Update previous state
        if (previousMobileState === null) {
          previousMobileState = mobile;
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [simulationRef]);

  return (
    <div ref={containerRef} style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
      <canvas
        id="snowCanvas"
        ref={canvasRef}
        width={1600}
        height={600}
        style={{
          display: 'block',
          border: '2px solid #333',
          borderRadius: '10px',
          boxShadow: 'none',
          maxWidth: '100%',
          height: 'auto',
          backgroundColor: '#212529'
        }}
        aria-label="Seasonal weather simulation canvas"
      />
    </div>
  );
}
